import React, { useRef } from 'react';
import { KPICard } from '../ui';
import { useHRPerformanceByRole } from '../../hooks/useHRQueries';
import LoadingSpinner from '../ui/LoadingSpinner';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface ComparisonPerformanceByRoleProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const ComparisonPerformanceByRole: React.FC<ComparisonPerformanceByRoleProps> = ({ 
  branchId,
  showLoading = true,
  onError 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useHRPerformanceByRole(branchId);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'comparison-performance-by-role';
  const componentTitle = 'مقایسه رضایت بر اساس نقش';

  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <LoadingSpinner variant="minimal" size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base border border-status-error p-4">
        <p className="text-status-error">خطا در بارگذاری مقایسه عملکرد بر اساس نقش</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const roleNames = {
    cashier: 'صندوقداران',
    courier: 'پیک‌ها',
    host: 'میزبان‌ها'
  };

  const roleColors = ['primary', 'warning', 'info'] as const;

  return (
    <div ref={chartRef} className="card-base card-padding">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="kpi"
          dashboardSource="hr"
          onInfo={() => openGuide(CHART_GUIDES.comparisonPerformanceByRole)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'hr',
            componentType: 'kpi',
            description: 'مقایسه میانگین عملکرد نقش‌های مختلف',
            props: { branchId, showLoading, onError },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
          className="mb-4"
        />
        <h3 className="title-card">{componentTitle}</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        {data.map((roleData: any, index: number) => (
          <KPICard
            key={roleData.role}
            title={roleNames[roleData.role as keyof typeof roleNames]}
            value={`${roleData.average}%`}
            variant={roleData.average >= 85 ? 'success' : roleData.average >= 70 ? roleColors[index] : 'error'}
            accentStyle="corner"
            className="p-2 min-h-0 transition-colors"
            aria-label={`میانگین رضایت ${roleNames[roleData.role as keyof typeof roleNames]}: ${roleData.average}%`}
          />
        ))}
      </div>
    </div>
  );
};

export default ComparisonPerformanceByRole;